import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, ViewChild, ViewEncapsulation } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forOwn } from "lodash";
import { NgxSpinnerService } from "ngx-spinner";
import { BehaviorSubject } from "rxjs";
import { AuthService } from "src/app/core/service/auth.service";
import { SnackbarService } from "src/app/core/service/snackbar.service";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { TranslationLoaderService } from "src/app/core/service/translation-loader.service";
import { IdentityRequestDTO } from "src/app/data/schema/Identity/IdentityRequestDTO";
import { IdentityRequestUsernameDTO } from "src/app/data/schema/Identity/IdentityRequestUsernameDTO";
import { UserCustomFieldDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldDTO";
import { UsuarioTO } from "src/app/data/schema/User/UsuarioTO";
import { DataAnalysisService } from "src/app/data/service/data-analysis/data-analysis.service";
import { ApiIdentityUserService } from "src/app/data/service/identity/api-identity-user.service";
import { HttpErrorResponseUtils } from "src/app/utils/HttpErrorResponseUtils";
import { UserCustomFieldsComponent } from "../user-custom-fields/user-custom-fields.component";
import { locale as english } from "./i18n/en-US";
import { locale as spanish } from "./i18n/es-ES";
import { locale as portuguese } from "./i18n/pt-BR";

@Component({
    selector: "app-identity-register-dialog",
    templateUrl: "./identity-register-dialog.component.html",
    styleUrls: ["./identity-register-dialog.component.scss"],
    encapsulation: ViewEncapsulation.None
})
export class IdentityRegisterDialogComponent {
    public userForm: UntypedFormGroup;
    public _validPassword: boolean;
    public origen: string;
    public userEmail: string;
    public disableClose: boolean;
    public showFieldsUserName = false;
    public emailIsNotRequired = false;

    private _password: string;
    private subscriptionId: string;
    private tokenInvite: string;
    private groups: string[];

    @ViewChild("appUserCustomFields") userCustomFieldsComponent: UserCustomFieldsComponent;
    userCustomFields$: BehaviorSubject<UserCustomFieldDTO[]>;
    user: UsuarioTO;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data,
        private _translationLoaderService: TranslationLoaderService,
        private identityUserService: ApiIdentityUserService,
        private subscriptionService: SubscriptionService,
        private dataAnalysisService: DataAnalysisService,
        private authService: AuthService,
        private snackBarService: SnackbarService,
        private spinnerService: NgxSpinnerService,
        private dialogRef: MatDialogRef<IdentityRegisterDialogComponent>
    ) {
        this._translationLoaderService.loadTranslations(english, portuguese, spanish);

        this.userCustomFields$ = new BehaviorSubject<UserCustomFieldDTO[]>([]);
        this.user = new UsuarioTO();

        this.subscriptionService.currentSubscriptionAsync().subscribe((sub) => {
            this.subscriptionId = sub?.id;

            this.userCustomFields$.next(
                sub?.settings?.userCustomFields?.filter((x) => x.useAsUsername || x.showOnRegister)
            );

            this.showFieldsUserName = sub?.initialRegistrationSettings?.showUserName || false;
            this.emailIsNotRequired = sub?.initialRegistrationSettings?.emailNotRequired || false;

            if (sub?.initialRegistrationSettings?.showUserName) {
                this.showFieldsUserName = true;
                this.userForm = new UntypedFormGroup({
                    username: new UntypedFormControl("", [Validators.required, Validators.email]),
                    name: new UntypedFormControl("", [Validators.required]),
                    surname: new UntypedFormControl("", [Validators.required])
                });
            } else
                this.userForm = new UntypedFormGroup({
                    username: new UntypedFormControl("", [Validators.required, Validators.email])
                });

            if (this.emailIsNotRequired && sub?.settings?.userCustomFields?.find((x) => x.useAsUsername))
                this.userForm.controls.username.setValidators([Validators.email]);
        });

        this.origen = data.origen;
        this.disableClose = data.disableClose;
        this.tokenInvite = data.tokenInvite || null;

        if (data.userEmail) {
            this.userForm.controls.username.setValue(data.userEmail);
            this.userForm.controls.username.disable();
        }

        this.groups = data.groups || null;
    }

    customRequired: ValidatorFn = (control: UntypedFormGroup): ValidationErrors | null => {
        if (this.emailIsNotRequired && this.userCustomFields$?.value?.length) {
            return null;
        }

        return !control.value && control.dirty ? { required: true } : null;
    };

    get showUserCustomFieldsForm(): boolean {
        return this.userCustomFields$?.value.length > 0;
    }

    trimUserName(): void {
        this.userForm.controls.username.setValue(this.userForm.controls.username.value.trim());
    }

    validPassword($event: boolean): void {
        this._validPassword = $event;
    }

    password(event: string): void {
        this._password = event;
    }

    Create(): void {
        if (this.userForm.invalid || !this._validPassword) return;
        this.spinnerService.show("create-identity");

        const usernames: IdentityRequestUsernameDTO[] = [];

        if (this.userForm.controls.username.value) {
            usernames.push({
                username: this.userForm.controls.username.value,
                usernameType: "Email"
            } as IdentityRequestUsernameDTO);
        }

        const userCustomField = this.userCustomFieldsComponent.exportValues();
        const subscriptionUsernameCustomFields =
            this.userCustomFields$.value?.filter((x) => x.useAsUsername)?.map((x) => x.fieldName) ?? [];

        if (userCustomField && subscriptionUsernameCustomFields.length > 0) {
            forOwn(userCustomField, (value, key) => {
                if (!value) return;

                if (subscriptionUsernameCustomFields.includes(key)) {
                    usernames.push({
                        username: value,
                        usernameType: key
                    } as IdentityRequestUsernameDTO);
                }
            });
        }

        const request: IdentityRequestDTO = {
            password: this._password,
            subscriptionId: this.subscriptionId,
            usernames: usernames,
            tokenInvite: this.tokenInvite,
            name: this.userForm.controls.name?.value || null,
            surname: this.userForm.controls.surname?.value || null,
            groups: this.groups
        };

        this.identityUserService.Register(request).subscribe(
            (session) => {
                this.authService.setSession(session);
                this.dataAnalysisService.CompleteRegistration(
                    this.origen,
                    null,
                    this.userForm.controls.username.value,
                    false
                );
                this.dataAnalysisService.login(
                    this.userForm.controls.username.value,
                    request.usernames.map((x) => x.username),
                    null
                );

                this.spinnerService.hide("create-identity");

                this.dialogRef.close(session);
            },
            (error: HttpErrorResponse) => {
                this.spinnerService.hide("create-identity");
                this.snackBarService.showMessage(HttpErrorResponseUtils.FormatErrorMessage(error), 5000);
            }
        );
    }

    getPasswordRegex(): string {
        return this.subscriptionService.currentSubscription?.loginSettings?.defaultLoginOptions?.passwordRegex;
    }

    getPasswordCustomRules(): string {
        return this.subscriptionService.currentSubscription?.loginSettings?.defaultLoginOptions?.passwordCustomRules;
    }
}
